import { Fragment } from 'react'
import type { ReactNode } from 'react'

import type { TeaGradeStockLot } from '@/lib/api/tea-grade-api'
import { Button } from '@/components/ui/button'
import { TableCell, TableRow } from '@/components/ui/table'
import { formatDateTimeVN, formatDateVN, formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    /** Từng lô còn tồn, server đã sắp: kho phân loại trước, trong kho sắp theo
     *  ngày nghiệp vụ tăng dần. */
    lots?: TeaGradeStockLot[]
    isLoading?: boolean
    /** Lỗi tải phải nói là lỗi. Hiện "không còn lô tồn" khi API hỏng là khẳng
     *  định sai về kho, không phải trạng thái trống. */
    isError?: boolean
    onRetry?: () => void
    /** Số cột của bảng cha — mọi dòng bung trải hết chiều ngang. */
    colSpan: number
}

/** Cột nhãn chiếm hai cột đầu của bảng cha (Mã + Tên loại). */
const LABEL_SPAN = 2

const ROW_CLASS = 'bg-muted/40 hover:bg-muted/40'

type Warehouse = TeaGradeStockLot['warehouse']

const WAREHOUSE_LABEL: Record<Warehouse, () => string> = {
    sorting: m.tea_grade_stock_warehouse_sorting,
    scenting: m.tea_grade_stock_warehouse_scenting,
}

/** API chỉ được mở rộng thêm: tab chạy bản cũ gặp kho mới thì hiện mã kho thay
 *  vì vỡ cả trang. */
function warehouseLabel(warehouse: string): string {
    return Object.hasOwn(WAREHOUSE_LABEL, warehouse)
        ? WAREHOUSE_LABEL[warehouse as Warehouse]()
        : warehouse
}

function StatusRow({
    colSpan,
    children,
}: {
    colSpan: number
    children: ReactNode
}) {
    return (
        <TableRow data-testid="tea-grade-stock-status" className={ROW_CLASS}>
            <TableCell colSpan={colSpan} className="py-3 pl-10">
                {children}
            </TableCell>
        </TableRow>
    )
}

/** Gom lô theo kho, GIỮ nguyên thứ tự server trả về (kho phân loại trước, và
 *  trong mỗi kho là lô cũ trước — đúng thứ tự xuất kho). */
function groupByWarehouse(lots: TeaGradeStockLot[]) {
    const groups: { warehouse: string; lots: TeaGradeStockLot[] }[] = []
    for (const lot of lots) {
        const last = groups.at(-1)
        if (last?.warehouse === lot.warehouse) last.lots.push(lot)
        else groups.push({ warehouse: lot.warehouse, lots: [lot] })
    }
    return groups
}

function sum(lots: TeaGradeStockLot[]): number {
    return lots.reduce((total, lot) => total + lot.quantity, 0)
}

/**
 * Các dòng bung ra dưới một phân loại: mỗi kho một dòng tiêu đề (tổng kg + số
 * bao), rồi TỪNG lô kèm mã lô, ngày và số kg còn lại.
 *
 * Chỉ có tổng theo kho thì thủ kho biết "còn 245,5 kg" mà không biết nằm ở bao
 * nào, nhập lúc nào — trong khi xuất kho là chọn theo lô.
 *
 * Ngày của hai kho là hai thứ khác nhau: kho phân loại là ngày tạo lô, kho lấy
 * hương là ngày lấy hương khai trên phiếu. Lô cũ không có phiếu mang ngày lấy
 * hương thì `date_source = 'lot_created'` và dòng ghi rõ đây là ngày tạo lô, để
 * không ai đọc một ngày suy ra như ngày xưởng khai.
 *
 * Render bằng chính dòng của bảng cha (không lồng bảng con) để đọc liền mạch
 * ngay dưới phân loại vừa bấm.
 */
export function TeaGradeStockRows({
    lots,
    isLoading,
    isError,
    onRetry,
    colSpan,
}: Props) {
    // Bốn trạng thái khác hẳn nhau: đang tải, lỗi, đã tải mà trống, có dữ liệu.
    if (isLoading) {
        return (
            <StatusRow colSpan={colSpan}>
                <span className="text-sm text-muted-foreground">
                    {m.common_loading()}
                </span>
            </StatusRow>
        )
    }
    if (isError) {
        return (
            <StatusRow colSpan={colSpan}>
                <div className="flex flex-wrap items-center gap-3">
                    <span role="alert" className="text-sm text-destructive">
                        {m.tea_grade_stock_error()}
                    </span>
                    {onRetry ? (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={onRetry}
                        >
                            {m.tea_grade_stock_retry()}
                        </Button>
                    ) : null}
                </div>
            </StatusRow>
        )
    }
    if (!lots?.length) {
        return (
            <StatusRow colSpan={colSpan}>
                <span className="text-sm text-muted-foreground">
                    {m.tea_grade_stock_empty()}
                </span>
            </StatusRow>
        )
    }

    return (
        <>
            {groupByWarehouse(lots).map((group) => (
                <Fragment key={group.warehouse}>
                    <TableRow
                        data-testid="tea-grade-stock-row"
                        className={ROW_CLASS}
                    >
                        <TableCell
                            colSpan={LABEL_SPAN}
                            className="py-1.5 pl-10 text-sm font-medium"
                        >
                            {/* Tên kho nằm dưới header "Mã"/"Tên loại": không
                                có nhãn ẩn thì trình đọc màn hình đọc nó như một
                                mã phân loại. */}
                            <span className="sr-only">
                                {m.tea_grade_stock_sr_label()}{' '}
                            </span>
                            {warehouseLabel(group.warehouse)}
                        </TableCell>
                        <TableCell
                            colSpan={colSpan - LABEL_SPAN}
                            className="py-1.5 text-sm"
                        >
                            <span className="tabular-nums">
                                {m.tea_grade_stock_quantity({
                                    quantity: formatQty(sum(group.lots)),
                                })}
                            </span>
                            <span className="ml-2 text-xs text-muted-foreground">
                                {m.tea_grade_stock_lot_count({
                                    count: group.lots.length,
                                })}
                            </span>
                        </TableCell>
                    </TableRow>

                    {group.lots.map((lot) => (
                        <TableRow
                            key={lot.lot_id}
                            data-testid="tea-grade-stock-lot"
                            className={ROW_CLASS}
                        >
                            <TableCell
                                colSpan={LABEL_SPAN}
                                className="py-1 pl-16 font-mono text-xs"
                            >
                                {lot.lot_code}
                            </TableCell>
                            <TableCell
                                colSpan={colSpan - LABEL_SPAN}
                                className="py-1 text-xs"
                            >
                                <span className="tabular-nums">
                                    {formatQty(lot.quantity)} kg
                                </span>
                                <span className="ml-3 text-muted-foreground">
                                    {lot.date_source === 'scenting_date'
                                        ? m.tea_grade_stock_lot_scented({
                                              // Ngày lấy hương là cột DATE:
                                              // xưởng khai ngày, không khai
                                              // giờ. In kèm giờ thì mọi lô hiện
                                              // đúng 07:00:00 — đó là nửa đêm
                                              // UTC đổi sang giờ VN, không phải
                                              // giờ xưởng làm.
                                              when: formatDateVN(
                                                  lot.occurred_at,
                                              ),
                                          })
                                        : m.tea_grade_stock_lot_created({
                                              when: formatDateTimeVN(
                                                  lot.occurred_at,
                                              ),
                                          })}
                                </span>
                            </TableCell>
                        </TableRow>
                    ))}
                </Fragment>
            ))}
        </>
    )
}
